import { Col, Div, Dropdown, Icon, Row, Switch as ToggleSwitch, Text } from '@pinpoint/design-kit';
import { charts } from '@pinpoint/onpoint-charts';
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import arraySupport from 'dayjs/plugin/arraySupport';

import CONSTANTS from 'src/store/constants';
import { Card, Message, Switch, Button } from 'src/components';
import { Rbac } from 'src/containers';
import { formatNumber } from 'src/functions';
import DefaultTheme from 'src/styles/DefaultTheme';

import { ApiResponseProp, ModelProp } from 'src/store/typings';
import { LabeledCountPanel } from './panel';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(arraySupport);

const today = dayjs.utc();
const labelsYearMonth = Array(12)
  .fill(0)
  .map((_, idx) => today.subtract(idx, 'M').format('YYYY-MMM'))
  .reverse();

function aggregateByPredicate(array, predicate, target) {
  return array.map((label) => {
    const filtered = target.filter(predicate(label));
    const count = filtered.reduce((acc, cur) => acc + cur.count, 0);
    return count === 0 ? null : count;
  });
}

const menuItems = CONSTANTS.REAL_TIME_API.USAGE.FILTERS.map((filter) => (
  <Div k={filter} key={filter}>
    {CONSTANTS.REAL_TIME_API.USAGE.FILTERS_DESC[filter]}
  </Div>
));

export function ApiUsageCard({
  client,
  config,
  configId,
  entries,
  masks,
  generateStatsStatus,
  usageReady,
  usageFilter,
  mtdMatchedCount,
  mtdTotalCount,
  tdyMatchedCount,
  tdyTotalCount,
  ytdMatchedCount,
  ytdTotalCount,
  toggleView,
  selectUsageFilter,
  loadRtApiUsage,
}) {
  const [rangeSelectionOpen, setRangeSelectionOpen] = useState(false);
  const [stackedTheme, setStackedTheme] = useState('pinpoint1');
  const [maskName, setMaskName] = useState('Total');

  const toggleMask = useCallback(() => {
    if (maskName === 'Matched') setMaskName('Total');
    else setMaskName('Matched');
  }, [maskName]);
  const toggleRangeSelectionOpen = useCallback(
    () => setRangeSelectionOpen(!rangeSelectionOpen),
    [setRangeSelectionOpen, rangeSelectionOpen]
  );

  const formatBillingCycleLabels = useCallback(
    (labels) => labels.map((label) => (label !== 'Cur' ? label.slice(0, -4) : label)),
    []
  );

  const chartData = useMemo(() => {
    /** @type {{ title: string; series: Array<{ name: string; values: (number | null)[] }>; labels: string[] }} */
    const data = {
      title: `Usage Over ${CONSTANTS.REAL_TIME_API.USAGE.FILTERS_DESC[usageFilter]}`,
      series: [],
      labels: [],
    };
    if (!client?.billingDay) return data;
    const defaultDays = CONSTANTS.REAL_TIME_API.USAGE.FILTERS_LABEL_LENGTH[usageFilter];
    const maskPrefix = CONSTANTS.REAL_TIME_API.USAGE.FILTERS_MASK[usageFilter];
    const isBillingCycleToDateFilter = [
      CONSTANTS.REAL_TIME_API.USAGE.FILTERS_MASK[CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_3_BILLING_CYCLES],
      CONSTANTS.REAL_TIME_API.USAGE.FILTERS_MASK[CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_6_BILLING_CYCLES],
      CONSTANTS.REAL_TIME_API.USAGE.FILTERS_MASK[CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_12_BILLING_CYCLES],
    ].includes(maskPrefix);

    // Pre-allocate labels for 7 days
    data.labels = Array(defaultDays)
      .fill(0)
      .map((_, idx) => today.subtract(idx, 'day').format('YYYY-MM-DD'))
      .reverse();
    // Default filter predicate just returns all values
    let predicate = (label) => (entry) => entry.period === label;

    if (usageFilter === CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_12_MONTHS) {
      // last 12 month case
      data.labels = Array(12)
        .fill(0)
        .map((_, idx) => today.subtract(idx, 'M').format('MMM'))
        .reverse();
      predicate = (label) => (entry) => {
        const index = data.labels.indexOf(label);
        const entryYearMonth = dayjs.utc(entry.period, 'YYYY-MM-DD').format('YYYY-MMM');
        return entryYearMonth === labelsYearMonth[index];
      };
    } else if (
      [
        CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_3_BILLING_CYCLES,
        CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_6_BILLING_CYCLES,
        CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_12_BILLING_CYCLES,
      ].includes(usageFilter)
    ) {
      let billingCycleStart = dayjs.utc([dayjs.utc().year(), dayjs.utc().month(), +client.billingDay]);
      billingCycleStart = today.isSameOrAfter(billingCycleStart)
        ? billingCycleStart
        : billingCycleStart.subtract(1, 'month');
      const billingCycleEnd = billingCycleStart.add(1, 'month');

      data.labels = Array(defaultDays)
        .fill(0)
        .map((_, idx) => billingCycleStart.subtract(idx, 'M').format('MMM DD YYYY'))
        .reverse();
      data.labels[defaultDays - 1] = 'Cur';

      predicate = (label) => (entry) => {
        const entryPeriod = dayjs.utc(entry.period, 'YYYY-MM-DD');
        // Handle current cycle entry
        if (entryPeriod.isBetween(billingCycleStart, billingCycleEnd, 'day', '[)') && label === 'Cur') return true;

        // Handle past cycle entry
        const labelPeriodStart = dayjs.utc(
          `${label.split(' ')[0]} ${client.billingDay} ${label.split(' ')[2]}`,
          `MMM-D-YYYY`
        );
        const labelPeriodEnd = labelPeriodStart.add(1, 'M');
        return entryPeriod.isBetween(labelPeriodStart, labelPeriodEnd, 'day', '[)');
      };
    }

    const totalFilter = entries.filter((_, idx) => masks[`${maskPrefix}${maskName}`][idx]);
    const errorFilter = entries.filter((_, idx) => masks[`${maskPrefix}Errors`][idx]);
    // When in the total view, we only need the primary filter
    if (maskName === 'Total') {
      data.series.push({
        name: 'All Requests',
        values: aggregateByPredicate(data.labels, predicate, totalFilter),
      });
      if (isBillingCycleToDateFilter)
        // Fix up billing to date labels
        data.labels = formatBillingCycleLabels(data.labels);
      return data;
    }

    // When in matched view but caching is not activated, only show Matched and Unmatched (uncached)
    if (client?.enableRealTimeAPICaching === false) {
      const unmatchedFilter = entries.filter((item, idx) => masks[`${maskPrefix}Unmatched`][idx] && !item.cached);
      const matchedFilter = entries.filter((item, idx) => masks[`${maskPrefix}${maskName}`][idx] && !item.cached);

      data.series.push({
        name: 'Matched',
        values: aggregateByPredicate(data.labels, predicate, matchedFilter),
      });
      data.series.push({
        name: 'Non-matched',
        values: aggregateByPredicate(data.labels, predicate, unmatchedFilter),
      });
      data.series.push({
        name: 'Errors',
        values: aggregateByPredicate(data.labels, predicate, errorFilter),
      });
      if (isBillingCycleToDateFilter)
        // Fix up billing to date labels
        data.labels = formatBillingCycleLabels(data.labels);
      return data;
    }

    // When in the matched view with caching activated, we need the matched and cached, unmatched and cached, matched
    // and uncached, and unmatched and uncached entries.
    const uncachedMatchedFilter = entries.filter((item, idx) => masks[`${maskPrefix}${maskName}`][idx] && !item.cached);
    const uncachedUnmatchedFilter = entries.filter((item, idx) => masks[`${maskPrefix}Unmatched`][idx] && !item.cached);
    const cachedMatchedFilter = entries.filter((item, idx) => masks[`${maskPrefix}${maskName}`][idx] && item.cached);
    const cachedUnmatchedFilter = entries.filter((item, idx) => masks[`${maskPrefix}Unmatched`][idx] && item.cached);

    data.series.push({
      name: 'Matched',
      values: aggregateByPredicate(data.labels, predicate, uncachedMatchedFilter),
    });
    data.series.push({
      name: 'Non-matched',
      values: aggregateByPredicate(data.labels, predicate, uncachedUnmatchedFilter),
    });
    data.series.push({
      name: 'Cached Matched',
      values: aggregateByPredicate(data.labels, predicate, cachedMatchedFilter),
    });
    data.series.push({
      name: 'Cached Non-matched',
      values: aggregateByPredicate(data.labels, predicate, cachedUnmatchedFilter),
    });
    data.series.push({
      name: 'Errors',
      values: aggregateByPredicate(data.labels, predicate, errorFilter),
    });
    if (isBillingCycleToDateFilter)
      // Fix up billing to date labels
      data.labels = formatBillingCycleLabels(data.labels);
    return data;
  }, [usageFilter, entries, masks, maskName, client, formatBillingCycleLabels]);

  const chartOptions = useMemo(() => {
    const options = {
      grid: {
        top: '10%',
        bottom: 112,
      },
      xAxis: {
        name: [
          CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_3_BILLING_CYCLES,
          CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_6_BILLING_CYCLES,
          CONSTANTS.REAL_TIME_API.USAGE.FILTER.LAST_12_BILLING_CYCLES,
        ].includes(usageFilter)
          ? 'Billing cycle beginning on'
          : 'Period',
        axisLabel: {
          show: true,
          interval: 'auto',
        },
      },
      yAxis: {
        name: 'Number of calls',
      },
      tooltip: {
        show: false,
      },
      title: {
        top: 0,
      },
      legend: {
        itemGap: 24,
        show: true,
      },
      // label: {
      //   position: 'top',
      // },
      useUTC: true,
    };
    if (chartData.series.length === 1)
      options.series = [
        {
          itemStyle: {
            color: DefaultTheme.colors.marigold,
          },
        },
      ];
    if (chartData.series.length === 3)
      options.series = [
        {
          itemStyle: {
            color: DefaultTheme.colors.green,
          },
        },
        {
          itemStyle: {
            color: DefaultTheme.colors.blueLight,
          },
        },
        {
          itemStyle: {
            color: DefaultTheme.colors.tagHit,
          },
        },
      ];
    if (chartData.series.length === 5)
      options.series = [
        {
          itemStyle: {
            color: DefaultTheme.colors.green,
          },
        },
        {
          itemStyle: {
            color: DefaultTheme.colors.blueLight,
          },
        },
        {
          itemStyle: {
            color: DefaultTheme.colors.greenDark2,
          },
        },
        {
          itemStyle: {
            color: '#2C6F96',
          },
        },
        {
          itemStyle: {
            color: DefaultTheme.colors.tagHit,
          },
        },
      ];
    return options;
  }, [chartData, usageFilter]);

  return (
    <Card
      title={
        usageReady === CONSTANTS.API.SUCCEEDED ? (
          <Div d="flex">
            <Text textWeight="600">{configId ? 'Usage:' : 'Total Usage'}</Text>
            <Text>&ensp;{config?.config_metadata?.model?.name}</Text>
          </Div>
        ) : null
      }
      iconProps={{
        'data-debug': 'card-icon-debug',
        name: configId ? 'Usage' : 'TotalUsage',
        color: 'white',
        size: '3.125rem',
        viewBox: '0 0 18 16',
      }}
      titleProps={{
        'data-debug': 'card-title-debug',
        bg: 'marigold',
        h: '5.5rem',
        p: { l: '2.5rem', r: '2.625rem' },
        rounded: { tr: 'md', tl: 'md' },
      }}
      titleTextProps={{
        'data-debug': 'card-title-text-debug',
        textColor: 'white',
        textSize: 'title',
        lineHeight: 'title',
      }}
      cardProps={{
        'data-debug': 'card-debug',
        p: '0',
        textSize: 'paragraph',
        style: {
          lineHeight: '1.5rem',
        },
        rounded: 'md',
        textColor: 'shade66',
        d: 'flex',
        flexDir: 'column',
      }}
    >
      <Switch status={usageReady}>
        <Message bg="white" value={CONSTANTS.API.STARTED} />
        <Message type="error" bg="white" value={CONSTANTS.API.FAILED} />
        <Col value={CONSTANTS.API.SUCCEEDED} gutterWidth="0" size="12" flexGrow="1" d="flex" flexDir="column">
          <Row p={{ t: '3.375rem', x: '2.5rem' }} flexDir="row">
            <Col gutterWidth="0" size="2" align="flex-start" d="flex" flexDir="column">
              <Rbac roles={CONSTANTS.ROLES.slice(0, 4)}>
                <Div d="flex">
                  <Button buttonStyle="admin" disabled>
                    <Icon name="RtUsage" viewBox="0 0 18 18" color="white" m={{ r: '.5rem' }} />
                    REQUESTS
                  </Button>
                  <Button buttonStyle="admin" onClick={toggleView}>
                    <Icon name="RtRetTime" viewBox="0 0 15 17" color="white" m={{ r: '.5rem' }} />
                    RETURN TIMES
                  </Button>
                </Div>
              </Rbac>
              <Div textColor="shade66" textSize="input" textWeight="500" p={{ t: '.75rem' }} m={{ y: '.25rem' }}>
                {maskName === 'Matched' ? 'Stats: Matched Only' : 'Stats: All Requests'}
              </Div>
            </Col>
            <Col size="7" textColor="shade66" textSize="small" data-debug="col" d="flex" align="center">
              <Div textColor="shade66" textSize="input" textWeight="500" alignSelf="baseline" p={{ l: '.5rem' }}>
                Show Usage for
              </Div>
              <Dropdown
                alignSelf="baseline"
                h="2rem"
                textSize="1rem"
                w="14rem"
                m={{ l: '1rem' }}
                isOpen={rangeSelectionOpen}
                onClick={toggleRangeSelectionOpen}
                defaultValue={menuItems[CONSTANTS.REAL_TIME_API.USAGE.FILTERS.findIndex((fl) => fl === usageFilter)]}
                menu={menuItems}
                onSelect={selectUsageFilter}
                rounded={rangeSelectionOpen ? { tl: 'md', bl: '0', br: '0', tr: 'md' } : 'md'}
                border={rangeSelectionOpen ? { x: '1px solid', t: '1px solid', b: '0' } : '1px solid'}
                style={{
                  transition: 'all 0.2s ease-in-out',
                }}
                menuItemOptions={{
                  p: { y: '.25rem', x: '.75rem' },
                  textWeight: '500',
                  bg: 'shadeF2',
                  hoverBg: 'white',
                  style: {
                    transition: 'all 0.2s ease-in-out',
                  },
                }}
                menuOptions={{
                  border: { b: '1px solid', x: '1px solid', t: '0' },
                  rounded: { bl: 'md', br: 'md', tr: '0', tl: '0' },
                  pos: 'absolute',
                  shadow: 4,
                  minW: '14rem',
                  maxW: '14rem',
                  style: {
                    transition: 'all 0.2s ease-in-out',
                  },
                  maxH: '250px',
                  overflow: 'auto',
                  textColor: 'shade66',
                  m: { t: '-2px', l: '-1px' },
                }}
              />
            </Col>
            <Col
              gutterWidth="0"
              textAlign="right"
              textColor="shade66"
              textSize="small"
              size="3"
              align="first baseline"
              d="flex"
            >
              <Row w="100%" m="0" align="center" justify="flex-end" p={{ t: '.25rem' }}>
                <Text textColor="shade66" textSize="input" textWeight="500" m={{ r: '.5rem' }}>
                  Show Breakdown
                </Text>
                <Div d="inline-block" m="0" cursor="pointer" onClick={toggleMask}>
                  <ToggleSwitch
                    checked={maskName === 'Matched'}
                    activeColor="marigold"
                    inactiveColor="shadeA0"
                    activeShadow="0"
                    inactiveShadow="0"
                    p="0"
                    m="0"
                  />
                </Div>
              </Row>
            </Col>
          </Row>
          <Row p={{ t: '.75rem', x: '2.5rem', b: '2rem' }} flexGrow="1">
            <Col size="2" d="flex" flexDir="column" justify="flex-start" align="center" gutterWidth="0">
              <Row m={{ b: '1rem' }} w="100%">
                <LabeledCountPanel
                  status={generateStatsStatus}
                  value={maskName === 'Matched' ? ytdMatchedCount : ytdTotalCount}
                  title="Year to Date"
                  panelProps={{
                    p: '1.5rem',
                    w: '100%',
                  }}
                  countProps={{
                    textWeight: '500',
                    textSize: 'display1',
                    textColor: 'black',
                  }}
                  titleProps={{
                    textWeight: '500',
                    textSize: 'body',
                    textColor: 'marigold',
                  }}
                />
              </Row>
              <Row m={{ b: '1rem' }} w="100%">
                <LabeledCountPanel
                  status={generateStatsStatus}
                  value={maskName === 'Matched' ? mtdMatchedCount : mtdTotalCount}
                  title="Month to Date"
                  panelProps={{
                    p: '1.5rem',
                    w: '100%',
                  }}
                  countProps={{
                    textWeight: '500',
                    textColor: 'black',
                    textSize: 'display1',
                  }}
                  titleProps={{
                    textWeight: '500',
                    textSize: 'body',
                    textColor: 'marigold',
                  }}
                />
              </Row>
              <Row w="100%">
                <LabeledCountPanel
                  status={generateStatsStatus}
                  value={maskName === 'Matched' ? tdyMatchedCount : tdyTotalCount}
                  title="Today"
                  panelProps={{
                    p: '1.5rem',
                    w: '100%',
                  }}
                  countProps={{
                    textWeight: '500',
                    textColor: 'black',
                    textSize: 'display1',
                  }}
                  titleProps={{
                    textWeight: '500',
                    textSize: 'body',
                    textColor: 'marigold',
                  }}
                />
              </Row>
            </Col>
            <Col size="10" d="flex" align="stretch" justify="stretch" gutterWidth="0">
              <Row w="100%" h="100%" m={{ l: '1rem' }}>
                <Col gutterWidth="0" border="1px solid" borderColor="shadeD9" rounded="md" size="12">
                  <Div p={{ t: '.5rem', r: '.5rem' }} m={{ b: '-2rem' }} h="100%">
                    {!!chartData?.series?.length && (
                      <charts.VerticalBar
                        {...chartData}
                        formatter={(params) => formatNumber(params.value)}
                        options={chartOptions}
                        theme={stackedTheme}
                        onThemeChange={setStackedTheme}
                        usePalette
                        showPalette={false}
                        markLine={false}
                      />
                    )}
                  </Div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Message
          value={null}
          bg="white"
          type="info"
          text="No data"
          cta={{ text: 'Refresh', onClick: loadRtApiUsage }}
        />
      </Switch>
    </Card>
  );
}

ApiUsageCard.defaultProps = {
  config: null,
  configId: null,
  generateStatsStatus: null,
  usageReady: null,
  mtdMatchedCount: null,
  mtdTotalCount: null,
  tdyMatchedCount: null,
  tdyTotalCount: null,
  ytdMatchedCount: null,
  ytdTotalCount: null,
};

ApiUsageCard.propTypes = {
  client: PropTypes.shape({
    billingDay: PropTypes.number.isRequired,
    enableRealTimeAPICaching: PropTypes.bool.isRequired,
  }).isRequired,
  // component
  usageFilter: PropTypes.string.isRequired,
  // rtApiUsage store
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      period: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      meta: PropTypes.string,
    })
  ).isRequired,
  masks: PropTypes.shape({
    ytdMatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    mtdMatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    mtdTotal: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    tdyMatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    tdyTotal: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    btdUnmatched: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
    btdErrors: PropTypes.arrayOf(PropTypes.bool.isRequired).isRequired,
  }).isRequired,
  usageReady: ApiResponseProp,
  generateStatsStatus: ApiResponseProp,
  configId: PropTypes.string,
  config: PropTypes.shape({
    id: PropTypes.string.isRequired,
    config_metadata: PropTypes.shape({
      model: ModelProp,
    }).isRequired,
  }),
  mtdMatchedCount: PropTypes.number,
  mtdTotalCount: PropTypes.number,
  tdyMatchedCount: PropTypes.number,
  tdyTotalCount: PropTypes.number,
  ytdMatchedCount: PropTypes.number,
  ytdTotalCount: PropTypes.number,
  // functions
  selectUsageFilter: PropTypes.func.isRequired,
  toggleView: PropTypes.func.isRequired,
  loadRtApiUsage: PropTypes.func.isRequired,
};
